import React, { useState, useEffect, useMemo } from "react";
import { 
  X, Plus, Edit2, Trash2, Check, Tag, Folder, RefreshCw, 
  AlertCircle, FileSpreadsheet, ChevronRight, ChevronDown, Search, GitBranch, List
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { categoryImporterService } from "../../services/categoryImporterService";
import CategoryImporterModal from "./CategoryImporterModal";

/**
 * TaxonomiaManagerModal.js — Cadastro & Gerenciamento de Taxonomia (Categorias iSet + Tipos de Joia)
 *
 * Permite visualizar, criar, editar, importar e organizar:
 * - Categorias de Produtos (`produto_categorias`) com código numérico iSet (`categories_id`), Pai (`parent_id`) e Slug.
 * - Suporta exibição em Árvore Hierárquica (Pai -> Subcategorias) e Lista.
 * - Tipos de Joia com Prefixos de SKU (`produto_tipos`).
 */
export default function TaxonomiaManagerModal({ isOpen, onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState("categorias"); // "categorias" | "tipos"
  const [viewMode, setViewMode] = useState("tree"); // "tree" | "list"
  
  const [categorias, setCategorias] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");

  const [isImporterOpen, setIsImporterOpen] = useState(false);
  const [isRelationUploading, setIsRelationUploading] = useState(false);
  const [relationProgress, setRelationProgress] = useState(null);
  const [relationSummary, setRelationSummary] = useState(null);

  // Form States para Nova Categoria
  const [newCatName, setNewCatName] = useState("");
  const [newCatId, setNewCatId] = useState("");
  const [newParentId, setNewParentId] = useState("0");
  const [newSlug, setNewSlug] = useState("");

  // Form States para Novo Tipo
  const [newTipoName, setNewTipoName] = useState("");
  const [newTipoPrefix, setNewTipoPrefix] = useState("");

  // Form States para Edição Inline
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCatId, setEditCatId] = useState("");
  const [editParentId, setEditParentId] = useState("0");
  const [editSlug, setEditSlug] = useState("");
  const [editPrefix, setEditPrefix] = useState("");

  // Nós expandidos na árvore
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Carrega dados das collections do PocketBase
  const loadTaxonomias = async () => {
    if (!isOpen) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const [catList, tipoList] = await Promise.all([
        pb.collection("produto_categorias").getFullList({ sort: "+nome" }),
        pb.collection("produto_tipos").getFullList({ sort: "+nome" })
      ]);
      setCategorias(catList || []);
      setTipos(tipoList || []);
    } catch (err) {
      console.error("Erro ao carregar taxonomias:", err);
      setErrorMsg("Erro de conexão ao carregar categorias e tipos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTaxonomias();
  }, [isOpen]);

  // Constrói árvore hierárquica das categorias
  const categoryTree = useMemo(() => {
    return categoryImporterService.buildTree(categorias);
  }, [categorias]);

  // Filtro de categorias por busca
  const filteredCategorias = useMemo(() => {
    if (!search.trim()) return categorias;
    const term = search.toLowerCase().trim();
    return categorias.filter(c => 
      c.nome?.toLowerCase().includes(term) ||
      String(c.categories_id || "").includes(term) ||
      String(c.parent_id || "").includes(term) ||
      c.slug?.toLowerCase().includes(term)
    );
  }, [categorias, search]);

  if (!isOpen) return null;

  const toggleNode = (nodeId) => {
    if (!nodeId) return;
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Criar Nova Categoria (com suporte a iSet ID e Parent)
  const handleCreateCategoria = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;

    setSaving(true);
    setErrorMsg("");
    try {
      const numId = newCatId.trim() ? Number(newCatId.trim()) || newCatId.trim() : null;
      const parentIdNum = Number(newParentId) || 0;

      await pb.collection("produto_categorias").create({
        nome: newCatName.trim(),
        categories_id: numId,
        parent_id: parentIdNum,
        slug: newSlug.trim(),
        ativo: true
      });

      setNewCatName("");
      setNewCatId("");
      setNewParentId("0");
      setNewSlug("");
      await loadTaxonomias();
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg("Erro ao cadastrar categoria: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Importação de Relação Produto <-> Categoria (relacao-produtos-categorias.xlsx)
  const handleRelationFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsRelationUploading(true);
    setRelationProgress({ current: 0, total: 100, sku: "Lendo arquivo..." });
    setRelationSummary(null);
    setErrorMsg("");

    try {
      const parsed = await categoryImporterService.parseRelationFile(file);
      const summary = await categoryImporterService.processRelationImport({
        prodToCatsMap: parsed.prodToCatsMap,
        onProgress: (p) => setRelationProgress(p)
      });
      setRelationSummary(summary);
      await loadTaxonomias();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Erro na importação de relação produtos-categorias:", err);
      setErrorMsg("Erro ao importar relações: " + err.message);
    } finally {
      setIsRelationUploading(false);
      setRelationProgress(null);
      e.target.value = "";
    }
  };

  // Salvar Edição de Categoria ou Tipo
  const handleSaveEdit = async (id, isCategory) => {
    if (!editName.trim()) return;

    setSaving(true);
    setErrorMsg("");
    try {
      if (isCategory) {
        const numId = editCatId !== "" ? Number(editCatId) || editCatId : null;
        const parentIdNum = Number(editParentId) || 0;

        await pb.collection("produto_categorias").update(id, {
          nome: editName.trim(),
          categories_id: numId,
          parent_id: parentIdNum,
          slug: editSlug.trim()
        });
      } else {
        await pb.collection("produto_tipos").update(id, {
          nome: editName.trim(),
          prefixo_sku: editPrefix.trim().toUpperCase()
        });
      }
      setEditingId(null);
      await loadTaxonomias();
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg("Erro ao atualizar registro: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle Ativo/Inativo
  const handleToggleAtivo = async (item, isCategory) => {
    try {
      const collectionName = isCategory ? "produto_categorias" : "produto_tipos";
      await pb.collection(collectionName).update(item.id, {
        ativo: !item.ativo
      });
      await loadTaxonomias();
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorMsg("Erro ao alterar status: " + err.message);
    }
  };

  // Excluir Registro
  const handleDelete = async (id, isCategory) => {
    if (!id) return;
    if (!window.confirm("Deseja realmente excluir esta opção?")) return;
    setSaving(true);
    setErrorMsg("");
    try {
      const collectionName = isCategory ? "produto_categorias" : "produto_tipos";
      await pb.collection(collectionName).delete(id);
      
      if (editingId === id) {
        setEditingId(null);
      }
      
      await loadTaxonomias();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Erro ao excluir registro de taxonomia:", err);
      setErrorMsg("Não foi possível excluir: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Componente recursivo para renderização de nó na árvore
  const renderTreeNode = (node, level = 0) => {
    const nodeKey = node.id || `node-${node.categories_id || Math.random()}`;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isEditing = editingId === node.id;

    return (
      <div key={nodeKey} className="space-y-1">
        <div 
          className={`p-2 rounded-lg flex items-center justify-between hover:bg-slate-50 transition-colors border ${
            level === 0 ? "border-slate-200 bg-slate-50/50" : "border-slate-100 bg-white ml-5"
          }`}
        >
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1 mr-2 flex-wrap">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Nome..."
                className="bg-white border border-blue-400 rounded px-2 py-1 font-semibold text-slate-900 text-xs flex-1 outline-none"
              />
              <input
                type="text"
                value={editCatId}
                onChange={(e) => setEditCatId(e.target.value)}
                placeholder="ID iSet (ex: 455)..."
                className="bg-white border border-blue-400 rounded px-2 py-1 font-mono text-xs w-24 outline-none"
              />
              <select
                value={editParentId}
                onChange={(e) => setEditParentId(e.target.value)}
                className="bg-white border border-blue-400 rounded px-2 py-1 text-xs w-36 outline-none"
              >
                <option value="0">Sem Categoria Pai (Raiz)</option>
                {categorias.filter(c => c.id !== node.id).map(c => (
                  <option key={c.id} value={c.categories_id || c.id}>
                    {c.nome} (ID: {c.categories_id || "N/A"})
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleSaveEdit(node.id, true)}
                className="p-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              >
                <Check size={14} />
              </button>
              <button onClick={() => setEditingId(null)} className="p-1 bg-slate-300 text-slate-700 rounded">
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              {hasChildren ? (
                <button 
                  onClick={() => toggleNode(node.id)}
                  className="p-1 text-slate-400 hover:text-slate-700 rounded"
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <div className="w-6" />
              )}
              <Folder size={15} className={level === 0 ? "text-blue-600" : "text-amber-500"} />
              <span className="font-bold text-slate-800 text-xs truncate">{node.nome}</span>

              {node.categories_id && (
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 font-mono font-bold rounded text-[10px]">
                  ID: {node.categories_id}
                </span>
              )}

              {hasChildren && (
                <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 font-bold rounded text-[10px]">
                  {node.children.length} subcategorias
                </span>
              )}

              {!node.ativo && (
                <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">Inativo</span>
              )}
            </div>
          )}

          <div className="flex items-center gap-1 shrink-0">
            {!isEditing && (
              <button
                onClick={() => {
                  setEditingId(node.id);
                  setEditName(node.nome);
                  setEditCatId(node.categories_id || "");
                  setEditParentId(String(node.parent_id || "0"));
                  setEditSlug(node.slug || "");
                }}
                className="p-1 text-slate-400 hover:text-blue-600 rounded hover:bg-slate-100"
                title="Editar Categoria"
              >
                <Edit2 size={13} />
              </button>
            )}
            <button
              onClick={() => handleToggleAtivo(node, true)}
              className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                node.ativo ? "bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-800" : "bg-emerald-100 text-emerald-800"
              }`}
            >
              {node.ativo ? "Desativar" : "Ativar"}
            </button>
            <button
              onClick={() => handleDelete(node.id, true)}
              className="p-1 text-slate-400 hover:text-red-600 rounded hover:bg-slate-100"
              title="Excluir"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="space-y-1">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* HEADER */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500 rounded-xl text-slate-900">
              <Tag size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight">Gerenciamento de Taxonomia & Categorias iSet</h2>
              <p className="text-xs text-slate-400">Categorias E-commerce (IDs, Hierarquia e Slugs) & Tipos de Joias</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* SUBABAS & CONTROLES DE VISUALIZAÇÃO */}
        <div className="bg-slate-100 border-b border-slate-200 px-6 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab("categorias")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold transition-all ${
                activeTab === "categorias" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Folder size={14} />
              <span>Categorias iSet ({categorias.length})</span>
            </button>

            <button
              onClick={() => setActiveTab("tipos")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold transition-all ${
                activeTab === "tipos" ? "bg-white text-amber-600 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Tag size={14} />
              <span>Tipos de Joias ({tipos.length})</span>
            </button>
          </div>

          {/* MODO DE EXIBIÇÃO E IMPORTAR (.XLSX) */}
          {activeTab === "categorias" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-slate-200 p-0.5 rounded-lg">
                <button
                  onClick={() => setViewMode("tree")}
                  className={`p-1.5 rounded-md font-bold flex items-center gap-1 ${
                    viewMode === "tree" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
                  }`}
                  title="Modo Hierarquia Árvore"
                >
                  <GitBranch size={13} />
                  <span className="text-[11px]">Árvore</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1.5 rounded-md font-bold flex items-center gap-1 ${
                    viewMode === "list" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
                  }`}
                  title="Modo Lista Simples"
                >
                  <List size={13} />
                  <span className="text-[11px]">Lista</span>
                </button>
              </div>

              <button
                onClick={() => setIsImporterOpen(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow transition-all"
                title="Importar catálogo de categorias iSet (categorias-modelo.xlsx)"
              >
                <FileSpreadsheet size={14} />
                <span>Importar Categorias (.xlsx)</span>
              </button>

              <label className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs shadow transition-all cursor-pointer">
                <FileSpreadsheet size={14} />
                <span>Importar Relação Produto-Categoria (.xlsx)</span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleRelationFileSelect}
                  className="hidden"
                  disabled={isRelationUploading}
                />
              </label>
            </div>
          )}
        </div>

        {/* CORPO */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-xs">
          
          {/* PROGRESSO DE RELAÇÕES */}
          {isRelationUploading && relationProgress && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-blue-900 font-bold">
                <span className="flex items-center gap-2">
                  <RefreshCw size={15} className="animate-spin text-blue-600" />
                  Sincronizando Relação Produtos ↔ Categorias...
                </span>
                <span className="font-mono">{relationProgress.current} / {relationProgress.total}</span>
              </div>
              <p className="text-[11px] text-blue-700 font-mono">Processando SKU: {relationProgress.sku}</p>
            </div>
          )}

          {relationSummary && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 space-y-1">
              <h4 className="font-bold text-xs flex items-center gap-1.5 text-emerald-800">
                <Check size={16} /> Relações Sincronizadas com Sucesso!
              </h4>
              <p className="text-[11px]">
                • Catálogo Atualizado com Categorias: <strong>{relationSummary.updatedCatalog}</strong> itens<br />
                • Fichas Canônicas Vinculadas: <strong>{relationSummary.updatedDados}</strong> SKUs
              </p>
            </div>
          )}

          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* TAB 1: CATEGORIAS */}
          {activeTab === "categorias" && (
            <div className="space-y-4">
              
              {/* FORMULÁRIO DE CRIAR CATEGORIA */}
              <form onSubmit={handleCreateCategoria} className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                <span className="font-bold text-blue-900 block text-[11px] uppercase tracking-wider">
                  + Cadastrar Nova Categoria Manualmente
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input
                    type="text"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Nome (ex: Namoro, Alianças)..."
                    className="bg-white border border-blue-300 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 sm:col-span-2"
                  />
                  <input
                    type="text"
                    value={newCatId}
                    onChange={(e) => setNewCatId(e.target.value)}
                    placeholder="ID iSet (ex: 458)..."
                    className="bg-white border border-blue-300 rounded-lg p-2 font-mono font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newParentId}
                    onChange={(e) => setNewParentId(e.target.value)}
                    className="bg-white border border-blue-300 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Sem Pai (Categoria Raiz)</option>
                    {categorias.map(c => (
                      <option key={c.id} value={c.categories_id || c.id}>
                        Pai: {c.nome} (ID: {c.categories_id || "N/A"})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <input
                    type="text"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value)}
                    placeholder="Slug / URL (ex: alianca-de-namoro)..."
                    className="bg-white border border-blue-300 rounded-lg p-2 text-slate-700 font-mono text-xs flex-1 outline-none"
                  />
                  <button
                    type="submit"
                    disabled={saving || !newCatName.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center gap-1 shadow-md disabled:opacity-50 transition-all shrink-0"
                  >
                    <Plus size={14} />
                    <span>Salvar Categoria</span>
                  </button>
                </div>
              </form>

              {/* BUSCA DE CATEGORIAS */}
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filtrar categorias por nome, ID ou slug..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:bg-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* MODO ÁRVORE HIERÁRQUICA */}
              {viewMode === "tree" && !search.trim() && (
                <div className="bg-white border border-slate-200 rounded-xl p-3 max-h-96 overflow-y-auto space-y-2 shadow-sm">
                  {categoryTree.length > 0 ? (
                    categoryTree.map(rootNode => renderTreeNode(rootNode, 0))
                  ) : (
                    <div className="text-center py-8 text-slate-400 font-medium">
                      Nenhuma categoria cadastrada. Clique em "Importar iSet (.xlsx)" para importar.
                    </div>
                  )}
                </div>
              )}

              {/* MODO LISTA DE CATEGORIAS */}
              {(viewMode === "list" || search.trim()) && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                    {filteredCategorias.map(cat => {
                      const isEditing = editingId === cat.id;
                      return (
                        <div key={cat.id} className="p-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                          {isEditing ? (
                            <div className="flex items-center gap-2 flex-1 mr-2 flex-wrap">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="bg-white border border-blue-400 rounded px-2 py-1 font-semibold text-slate-900 text-xs flex-1 outline-none"
                              />
                              <input
                                type="text"
                                value={editCatId}
                                onChange={(e) => setEditCatId(e.target.value)}
                                placeholder="ID iSet..."
                                className="bg-white border border-blue-400 rounded px-2 py-1 font-mono text-xs w-24 outline-none"
                              />
                              <button
                                onClick={() => handleSaveEdit(cat.id, true)}
                                className="p-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                              >
                                <Check size={14} />
                              </button>
                              <button onClick={() => setEditingId(null)} className="p-1 bg-slate-300 text-slate-700 rounded">
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 min-w-0">
                              <Folder size={15} className="text-blue-600 shrink-0" />
                              <span className="font-bold text-slate-800 truncate">{cat.nome}</span>
                              
                              {cat.categories_id && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 font-mono font-bold rounded text-[10px] shrink-0">
                                  ID: {cat.categories_id}
                                </span>
                              )}

                              {cat.parent_id && cat.parent_id !== 0 && cat.parent_id !== "0" && (
                                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 font-bold rounded text-[10px] shrink-0">
                                  Pai ID: {cat.parent_id}
                                </span>
                              )}

                              {!cat.ativo && <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded shrink-0">Inativo</span>}
                            </div>
                          )}

                          <div className="flex items-center gap-1 shrink-0">
                            {!isEditing && (
                              <button
                                onClick={() => {
                                  setEditingId(cat.id);
                                  setEditName(cat.nome);
                                  setEditCatId(cat.categories_id || "");
                                  setEditParentId(String(cat.parent_id || "0"));
                                  setEditSlug(cat.slug || "");
                                }}
                                className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-slate-100"
                                title="Editar"
                              >
                                <Edit2 size={13} />
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleAtivo(cat, true)}
                              className={`px-2 py-1 text-[10px] font-bold rounded ${cat.ativo ? "bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                            >
                              {cat.ativo ? "Desativar" : "Ativar"}
                            </button>
                            <button
                              onClick={() => handleDelete(cat.id, true)}
                              className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-slate-100"
                              title="Excluir"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* TAB 2: TIPOS DE JOIA */}
          {activeTab === "tipos" && (
            <div className="space-y-4">
              
              {/* FORMULÁRIO DE CRIAR TIPO DE JOIA */}
              <form onSubmit={handleCreateTipo} className="bg-amber-50 border border-amber-200 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  value={newTipoName}
                  onChange={(e) => setNewTipoName(e.target.value)}
                  placeholder="Nome do Tipo (ex: Brinco)..."
                  className="bg-white border border-amber-300 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-amber-500"
                />
                <input
                  type="text"
                  value={newTipoPrefix}
                  onChange={(e) => setNewTipoPrefix(e.target.value)}
                  placeholder="Prefixo SKU (ex: BRI)..."
                  className="bg-white border border-amber-300 rounded-lg p-2 font-mono font-bold text-slate-800 uppercase outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="submit"
                  disabled={saving || !newTipoName.trim() || !newTipoPrefix.trim()}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold flex items-center justify-center gap-1 shadow-md disabled:opacity-50 transition-all"
                >
                  <Plus size={14} />
                  <span>Cadastrar Tipo</span>
                </button>
              </form>

              {/* TABELA DE TIPOS */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                  {tipos.map(t => {
                    const isEditing = editingId === t.id;
                    return (
                      <div key={t.id} className="p-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        {isEditing ? (
                          <div className="flex items-center gap-2 flex-1 mr-2">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="bg-white border border-amber-400 rounded px-2 py-1 font-semibold text-slate-900 w-1/2 outline-none"
                            />
                            <input
                              type="text"
                              value={editPrefix}
                              onChange={(e) => setEditPrefix(e.target.value)}
                              className="bg-white border border-amber-400 rounded px-2 py-1 font-mono font-bold text-slate-900 uppercase w-1/4 outline-none"
                            />
                            <button
                              onClick={() => handleSaveEdit(t.id, false)}
                              className="p-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                            >
                              <Check size={14} />
                            </button>
                            <button onClick={() => setEditingId(null)} className="p-1 bg-slate-300 text-slate-700 rounded">
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Tag size={15} className="text-amber-500" />
                            <span className="font-bold text-slate-800">{t.nome}</span>
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded font-mono font-bold text-[10px]">
                              {t.prefixo_sku}
                            </span>
                            {!t.ativo && <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">Inativo</span>}
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          {!isEditing && (
                            <button
                              onClick={() => {
                                setEditingId(t.id);
                                setEditName(t.nome);
                                setEditPrefix(t.prefixo_sku || "");
                              }}
                              className="p-1.5 text-slate-400 hover:text-amber-600 rounded hover:bg-slate-100"
                              title="Editar"
                            >
                              <Edit2 size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleAtivo(t, false)}
                            className={`px-2 py-1 text-[10px] font-bold rounded ${t.ativo ? "bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                          >
                            {t.ativo ? "Desativar" : "Ativar"}
                          </button>
                          <button
                            onClick={() => handleDelete(t.id, false)}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-slate-100"
                            title="Excluir"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end">
          <button onClick={onClose} className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs shadow-md transition-all">
            Concluído
          </button>
        </div>

      </div>

      {/* MODAL DE IMPORTAÇÃO DE PLANILHA ISET (.XLSX) */}
      <CategoryImporterModal
        isOpen={isImporterOpen}
        onClose={() => setIsImporterOpen(false)}
        onSuccess={() => {
          setIsImporterOpen(false);
          loadTaxonomias();
          if (onSuccess) onSuccess();
        }}
      />
    </div>
  );
}
